// Collision: skater vs goalie body.
//
// Owns:  the goalie's physical body as a solid obstacle for skaters.
// Reads: tuning.player.radius, caller-supplied goalie pos + radius.
// Mutates: player.pos + player.vel.
// Emits:  no events.
//
// Goalies are immovable from a skater's POV (think of them as fixed
// obstacles), so all separation is applied to the skater. Without this a
// carrier could skate through the goalie's body, dribble the puck inside
// the crease, and shoot from inside the net — classic "goalie interference"
// cheese. The goalie body radius matches the puck save volume so this
// collision lines up with the goalie's puck-handling reach.
//
// Note: the puck's own collision against the goalie (saves on Shot mode,
// reflections on Pass/Loose mode) lives in `goalie/save-volumes.ts`. This
// file only handles SKATER vs goalie body.
//
// Zero-allocation.

import { tuning } from "../state/tuning.js";
import type { PlayerState } from "../state/types.js";

/**
 * Resolves a skater overlapping a goalie's body — pushes the skater out
 * along the contact normal and removes the velocity component into the
 * goalie. Goalies are immovable from a skater's POV (think of them as
 * fixed obstacles), so all separation is applied to the skater.
 *
 * Zero-allocation.
 */
export function resolveSkaterVsGoalie(
  player: PlayerState,
  goalieX: number,
  goalieY: number,
  goalieRadius: number,
): void {
  const dx = player.pos.x - goalieX;
  const dy = player.pos.y - goalieY;
  const distSq = dx * dx + dy * dy;
  const contact = goalieRadius + tuning.player.radius;
  if (distSq >= contact * contact) return;
  if (distSq < 1e-12) {
    // Coincident — pick an arbitrary axial direction so the next tick
    // resolves cleanly.
    player.pos.x = goalieX + contact;
    return;
  }
  const dist = Math.sqrt(distSq);
  const nx = dx / dist;
  const ny = dy / dist;
  const overlap = contact - dist;
  player.pos.x += nx * overlap;
  player.pos.y += ny * overlap;
  const intoGoalie = -(player.vel.x * nx + player.vel.y * ny);
  if (intoGoalie > 0) {
    player.vel.x += nx * intoGoalie;
    player.vel.y += ny * intoGoalie;
  }
}
